import { memo, useCallback, useEffect, useState } from "react";
import { FleetTireSetRow } from "services/api/fleet/tires/types";
import { getFleetTiresSetAPI } from "services/api/fleet/tires/api";
import { PaginationDirection } from "services/url";
import { Errors } from "services/forms/validation/types";
import { Loader } from "ui/components/Loader";
import { TableSearch } from "ui/components/Table/TableSearch";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import { TIRE_TYPE_BACKGROUND } from "ui/modules/fleet/tires/pages/TireSetSearchPage/components/TireSetSearchResults/consts";
import { DemountFormValues } from "../types";

interface DemountStep2Props {
  formValues: DemountFormValues;
  tireSetId: string;
  onChange: (values: Partial<DemountFormValues>) => void;
  errors: Errors;
}

const DemountStep2: React.FC<DemountStep2Props> = ({ formValues, tireSetId, onChange, errors }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [availableSets, setAvailableSets] = useState<FleetTireSetRow[]>([]);
  const selectedRows = formValues.newTireSetId ? [formValues.newTireSetId] as any : [];

  useEffect(() => {
    fetchAvailableSets();
  }, []);

  const handleSelectedChange = useCallback((id: number | number[]) => {
    const setId = String(id);
    onChange({ newTireSetId: setId });
  }, [onChange]);

  const fetchAvailableSets = async () => {
    setIsLoading(true);
    try {
      const results = await getFleetTiresSetAPI({
        pageNumber: 1, pageSize: 100,
        sort: { sortBy: "created", direction: PaginationDirection.Descending },
      });
      setAvailableSets(results.data.filter((s) => s.id !== tireSetId && s.location?.entityName !== "Vehicle"));
    } catch { setAvailableSets([]); }
    setIsLoading(false);
  };

  const handleRowClick = useCallback((id: string) => {
    onChange({ newTireSetId: id });
  }, [onChange]);

  const columns: Column[] = [
    {
      title: "Kod",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetTireSetRow) => <span className="text-sm font-semibold">{row.code}</span>,
    },
    {
      title: "Dimenzija",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetTireSetRow) => {
        const dim = Array.from(new Set(row.childRows?.map((r) => `${r.width}/${r.height} R${r.radius}`).filter(Boolean))).join(", ");
        return <span className="text-sm">{dim || "-"}</span>;
      },
    },
    {
      title: "Tip",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetTireSetRow) => {
        const tireType = row.childRows?.[0]?.tireType;
        return (
          <div className="flex items-center gap-1.5 text-sm">
            {tireType && TIRE_TYPE_BACKGROUND(tireType.id)}
            {tireType?.name ?? "-"}
          </div>
        );
      },
    },
    {
      title: "Proizvođač",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetTireSetRow) => {
        const mfr = Array.from(new Set(row.childRows?.map((r) => r.tireManufacturer?.name).filter(Boolean))).join(", ");
        return <span className="text-sm">{mfr || "-"}</span>;
      },
    },
    {
      title: "Lokacija",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetTireSetRow) => <span className="text-sm text-slate-500">{row.location?.destination ?? "-"}</span>,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">Odaberite novi set za vozilo</h1>
          <p className="text-center text-md text-slate-500 mb-8">
            Vozilo mora uvijek imati montirane gume. Odaberite set sa skladišta.
          </p>
          <div className="relative h-[200px]"><Loader isLoading={true}>{null}</Loader></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">Odaberite novi set za vozilo</h1>
        <p className="text-center text-md text-slate-500 mb-8">
          Vozilo mora uvijek imati montirane gume. Odaberite set sa skladišta.
        </p>

        {errors.newTireSetId && (
          <p className="text-sm text-red-600 text-center mt-3">{errors.newTireSetId}</p>
        )}

        <div className="bg-white rounded-sm overflow-hidden mt-3">
          <TableSearch
            columns={columns}
            data={availableSets}
            selectableRows={true}
            showSelectAll={false}
            selectedRows={selectedRows}
            onSelectedChange={handleSelectedChange}
            disablePagination={true}
            isLoading={false}
            onRowClick={handleRowClick}
          />
        </div>

        {availableSets.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm text-slate-400">Nema dostupnih setova guma na skladištu.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(DemountStep2);
